import { useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Scan, ZoomIn, ZoomOut } from 'lucide-react';
import { useZoomPan } from '@/components/climbs/board-grid/useZoomPan';
import type { BoardLedLine } from '../../../types/device';
import { holdAt, lineHandles, snapPoint, type HoldShape, type PhotoPoint } from './led-lines';

const CONTROL =
  'flex items-center justify-center rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70 disabled:opacity-40 disabled:hover:bg-black/50';

/**
 * What a press on the photo does: draw the lines, add a joint to a line where
 * it crosses the hold pressed, or flip a hold's LED.
 */
export type LedLinesMode = 'draw' | 'split' | 'unlit';

interface BoardLedLinesEditorProps {
  /** The uploaded photo, the scan's own picture. */
  imageUrl: string;
  /** The holds a line may take — the scan's shown ones. They are the magnets. */
  holds: HoldShape[];
  /** Holds with no LED behind them. */
  unlit: ReadonlySet<number>;
  lines: BoardLedLine[];
  /** Every taken hold's LED, by place. */
  leds: ReadonlyMap<number, number>;
  /** The holds each line took, for marking the selected line's. */
  ledHolds: number[][];
  selectedLine: number | null;
  /** Where the next line starts, once clicked, when it does not follow on. */
  pendingStart: PhotoPoint | null;
  mode: LedLinesMode;
  /** A click on the photo, already pulled onto the hold it was near. */
  onPlace: (point: PhotoPoint) => void;
  /** An end of a line dragged to `to`, already pulled onto the hold it is near. */
  onMoveEnd: (line: number, point: number, to: PhotoPoint) => void;
  onSelectLine: (line: number | null) => void;
  /** A hold pressed in the "add a joint" mode: the line through it bends there. */
  onSplit: (place: number) => void;
  onToggleUnlit: (place: number) => void;
}

/**
 * **The LED lines of a scanned wall**, drawn on its photo
 * (docs/SPRAY_WALL_SCAN_PLAN.md), the way the board panel draws its lines:
 *
 * - each line has a start and an end, and **the lines are drawn as one run** —
 *   the first click puts the start down, every click after it closes a line
 *   and opens the next from where it stopped;
 * - **the ends are handles**, one per joint, and dragging one carries the line
 *   on either side of it;
 * - **the holds are magnets**: a click or a drag that comes near a hold lands
 *   on its centre (`snapPoint`), on bare wall it stays put — a turn between
 *   two panels.
 *
 * The holds the lines took wear an amber outline, the ones with no LED a grey
 * dashed one, and the selected line's carry their LED numbers. The zoom is the
 * board editor's (`useZoomPan`): ⌘ and scroll, then scroll to move.
 */
export function BoardLedLinesEditor({
  imageUrl,
  holds,
  unlit,
  lines,
  leds,
  ledHolds,
  selectedLine,
  pendingStart,
  mode,
  onPlace,
  onMoveEnd,
  onSelectLine,
  onSplit,
  onToggleUnlit,
}: BoardLedLinesEditorProps) {
  const { t } = useTranslation();
  const zoom = useZoomPan();
  const [dragging, setDragging] = useState<{ line: number; point: number } | null>(null);
  // The click a drag ends with lands on the photo: it is the end of the drag,
  // not a new point.
  const justDragged = useRef(false);

  const selectedPlaces = useMemo(
    () => new Set(selectedLine === null ? [] : ledHolds[selectedLine] ?? []),
    [ledHolds, selectedLine]
  );
  // The numbers worn: the selected line's — and every line's while adding a
  // joint, since any of them is a place to press.
  const numbered = useMemo(
    () => (mode === 'split' ? new Set(ledHolds.flat()) : selectedPlaces),
    [mode, ledHolds, selectedPlaces]
  );
  const handles = useMemo(() => lineHandles(lines), [lines]);

  const pointAt = (clientX: number, clientY: number): PhotoPoint | null => {
    const point = zoom.toContent(clientX, clientY);
    if (!point) return null;
    return { x: Math.min(1, Math.max(0, point.x)), y: Math.min(1, Math.max(0, point.y)) };
  };

  // The drag is followed on the window, so an end can be pulled past the edge
  // of the frame and still land on the photo's edge.
  const moveRef = useRef(onMoveEnd);
  moveRef.current = onMoveEnd;
  const pointAtRef = useRef(pointAt);
  pointAtRef.current = pointAt;
  const holdsRef = useRef(holds);
  holdsRef.current = holds;
  useEffect(() => {
    if (!dragging) return;

    const move = (event: globalThis.PointerEvent) => {
      const point = pointAtRef.current(event.clientX, event.clientY);
      if (point) moveRef.current(dragging.line, dragging.point, snapPoint(holdsRef.current, point));
    };
    const up = () => {
      justDragged.current = true;
      setDragging(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    const point = pointAt(event.clientX, event.clientY);
    if (!point) return;

    if (mode !== 'draw') {
      // The hold pressed — its LED number sits on it and lets the press
      // through, so pressing the number is pressing the hold.
      const hold = holdAt(holds, point);
      if (hold) (mode === 'split' ? onSplit : onToggleUnlit)(hold.place);
      return;
    }
    onPlace(snapPoint(holds, point));
  };

  const startDrag = (event: PointerEvent<HTMLButtonElement>, line: number, point: number) => {
    // The press is the handle's, not the start of a pan.
    event.stopPropagation();
    zoom.cancelPan();
    justDragged.current = false;
    onSelectLine(line);
    setDragging({ line, point });
  };

  return (
    <div
      {...zoom.frameProps}
      data-testid="board-led-lines-viewport"
      className="relative w-full select-none overflow-hidden rounded-md border bg-muted"
    >
      <div
        data-testid="board-led-lines-frame"
        {...zoom.layerProps}
        className={`relative w-full ${dragging ? 'cursor-grabbing' : mode === 'draw' ? 'cursor-crosshair' : 'cursor-pointer'}`}
        onClick={handleClick}
      >
        <img src={imageUrl} alt="" className="block w-full" draggable={false} />

        <svg
          className="pointer-events-none absolute inset-0 size-full"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          aria-hidden
        >
          {holds.map((hold) => {
            const isUnlit = unlit.has(hold.place);
            const lit = leds.has(hold.place);
            return (
              <polygon
                key={hold.place}
                data-testid={`board-led-hold-${hold.place}`}
                data-state={isUnlit ? 'unlit' : lit ? 'lit' : 'free'}
                points={hold.outline.map((p) => `${p.x},${p.y}`).join(' ')}
                fill={lit ? 'rgba(245, 158, 11, 0.25)' : 'none'}
                stroke={isUnlit ? '#9ca3af' : lit ? '#f59e0b' : 'rgba(255, 255, 255, 0.85)'}
                strokeWidth={selectedPlaces.has(hold.place) ? 2.5 : 1.25}
                strokeDasharray={isUnlit ? '3 3' : undefined}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {lines.map((line, index) => (
            <polyline
              key={index}
              points={line.points.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={index === selectedLine ? '#2563eb' : '#60a5fa'}
              strokeWidth={index === selectedLine ? 3 : 2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        {/* The selected line's LED numbers, as text outside the stretched SVG
            so it is not squashed with the photo. Counter-scaled like a handle,
            so it stays readable at any zoom. */}
        {holds
          .filter((hold) => numbered.has(hold.place))
          .map((hold) => (
            <span
              key={hold.place}
              className="pointer-events-none absolute rounded bg-blue-600 px-1 text-[10px] font-semibold leading-4 text-white"
              style={{
                left: `${hold.centre.x * 100}%`,
                top: `${hold.centre.y * 100}%`,
                transform: `translate(-50%, -50%) scale(${1 / zoom.scale})`,
              }}
            >
              {leds.get(hold.place)}
            </span>
          ))}

        {/* The ends of the lines, one handle per joint. Only while drawing:
            in the "no LED" mode a press is about the hold under it. */}
        {mode === 'draw' &&
          handles.map(({ line, point, at }) => (
            <button
              key={`${line}-${point}`}
              type="button"
              data-testid="board-led-line-end"
              aria-label={t('gymAdmin.boardDashboard.grid.ledLines.end', { number: line + 1 })}
              className={`absolute size-4 touch-none rounded-full border-2 border-white shadow ${
                line === selectedLine ? 'bg-blue-600' : 'bg-blue-400'
              } ${dragging?.line === line && dragging.point === point ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{
                left: `${at.x * 100}%`,
                top: `${at.y * 100}%`,
                transform: `translate(-50%, -50%) scale(${1 / zoom.scale})`,
              }}
              onPointerDown={(event) => startDrag(event, line, point)}
              onClick={(event) => event.stopPropagation()}
            />
          ))}

        {/* Where the next line starts, put down and waiting for its end. */}
        {mode === 'draw' && pendingStart && (
          <span
            data-testid="board-led-line-pending"
            className="pointer-events-none absolute size-4 rounded-full border-2 border-white bg-amber-500 shadow"
            style={{
              left: `${pendingStart.x * 100}%`,
              top: `${pendingStart.y * 100}%`,
              transform: `translate(-50%, -50%) scale(${1 / zoom.scale})`,
            }}
          />
        )}
      </div>

      <div
        className="absolute left-2 top-1/2 flex -translate-y-1/2 flex-col gap-1"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={zoom.zoomIn}
          disabled={!zoom.canZoomIn}
          title={t('climbs.board.zoom.in')}
          aria-label={t('climbs.board.zoom.in')}
          className={CONTROL}
        >
          <ZoomIn className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={zoom.zoomOut}
          disabled={!zoom.zoomed}
          title={t('climbs.board.zoom.out')}
          aria-label={t('climbs.board.zoom.out')}
          className={CONTROL}
        >
          <ZoomOut className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={zoom.reset}
          disabled={!zoom.zoomed}
          title={t('climbs.board.zoom.reset')}
          aria-label={t('climbs.board.zoom.reset')}
          className={CONTROL}
        >
          <Scan className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
